import { useState } from 'react';
import { cn } from '@/lib/format.ts';

/**
 * A screenshot a routine reported.
 *
 * The URL belongs to somebody else's host — a preview build, an object store,
 * a CI artifact — so it will sometimes be gone, expired or behind a login. The
 * browser's default for that is a broken-image glyph with the alt text spilling
 * across the layout, which looks like the app is broken rather than the link.
 * The design's diagonal stripe is the honest answer: something was here.
 */
export function Shot({
  url,
  label,
  className,
  variant = 'card',
}: {
  url: string;
  label: string | null;
  className?: string;
  variant?: 'card' | 'dark';
}) {
  const [failed, setFailed] = useState(false);

  const stripe =
    variant === 'card'
      ? 'repeating-linear-gradient(45deg, #ccd0e4 0 5px, #d8dcec 5px 10px)'
      : 'repeating-linear-gradient(45deg, #4c5478 0 6px, #565f85 6px 12px)';

  if (failed) {
    return (
      <span
        role="img"
        aria-label={label ? `${label} (could not be loaded)` : 'screenshot could not be loaded'}
        title={`Could not load ${url}`}
        className={cn(
          'grid place-items-center rounded-md border',
          variant === 'card' ? 'border-cardbtnbd' : 'border-inputbd',
          className,
        )}
        style={{ background: stripe }}
      >
        <span
          className={cn(
            'font-mono text-[9px] tracking-wide',
            variant === 'card' ? 'text-carddim' : 'text-faint',
          )}
        >
          gone
        </span>
      </span>
    );
  }

  return (
    <img
      src={url}
      alt={label ?? 'Screenshot'}
      loading="lazy"
      onError={() => setFailed(true)}
      className={cn(
        'rounded-md border object-cover',
        variant === 'card' ? 'border-cardbtnbd' : 'border-inputbd',
        className,
      )}
      style={{ background: stripe }}
    />
  );
}
